import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { del, get, post, put } from "../services/apiClient";
import { RoleSummary, StaffMember } from "../rbac/types";
import { useAuth } from "../contexts/AuthContext";

const ROLES_KEY = ["rbac", "roles"] as const;
const STAFF_KEY = ["rbac", "staff"] as const;
const MODULES_KEY = ["rbac", "modules"] as const;

export interface RbacModuleMeta {
  modules: Array<{ key: string; actions: string[] }>;
}

export const useRbacModules = () =>
  useQuery<RbacModuleMeta>({
    queryKey: MODULES_KEY,
    queryFn: () => get<RbacModuleMeta>("/admin/rbac/modules"),
  });

export const useRoles = () =>
  useQuery<RoleSummary[]>({
    queryKey: ROLES_KEY,
    queryFn: () => get<RoleSummary[]>("/admin/rbac/roles"),
  });

export const useRole = (id: string | null) =>
  useQuery<RoleSummary>({
    queryKey: ["rbac", "role", id],
    enabled: !!id,
    queryFn: () => get<RoleSummary>(`/admin/rbac/roles/${id}`),
  });

export interface RolePermissionInput {
  module: string;
  canView?: boolean;
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canExport?: boolean;
  canApprove?: boolean;
  canReject?: boolean;
  canSuspend?: boolean;
  canModerate?: boolean;
  canRefund?: boolean;
  canBroadcast?: boolean;
}

export interface CreateRoleInput {
  name: string;
  description?: string;
  copyFromRoleId?: string;
  permissions?: RolePermissionInput[];
}

export interface UpdateRoleInput {
  name?: string;
  description?: string;
  permissions?: RolePermissionInput[];
}

export const useCreateRole = () => {
  const qc = useQueryClient();
  return useMutation<RoleSummary, Error, CreateRoleInput>({
    mutationFn: (data) => post<RoleSummary>("/admin/rbac/roles", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLES_KEY }),
  });
};

export const useUpdateRole = () => {
  const qc = useQueryClient();
  const { refreshPermissions } = useAuth();
  return useMutation<RoleSummary, Error, { id: string; data: UpdateRoleInput }>(
    {
      mutationFn: ({ id, data }) =>
        put<RoleSummary>(`/admin/rbac/roles/${id}`, data),
      onSuccess: async () => {
        qc.invalidateQueries({ queryKey: ROLES_KEY });
        // If we edited our own role's siblings, our permissions may have shifted.
        await refreshPermissions();
      },
    },
  );
};

export const useDeleteRole = () => {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id) => del<{ success: boolean }>(`/admin/rbac/roles/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ROLES_KEY }),
  });
};

export const useStaff = () =>
  useQuery<StaffMember[]>({
    queryKey: STAFF_KEY,
    queryFn: () => get<StaffMember[]>("/admin/rbac/staff"),
  });

export interface CreateStaffInput {
  name: string;
  email: string;
  password: string;
  phone: string;
  roleId: string;
}

export interface UpdateStaffInput {
  name?: string;
  roleId?: string;
  isActive?: boolean;
  password?: string;
}

export const useCreateStaff = () => {
  const qc = useQueryClient();
  return useMutation<StaffMember, Error, CreateStaffInput>({
    mutationFn: (data) => post<StaffMember>("/admin/rbac/staff", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STAFF_KEY });
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
};

export const useUpdateStaff = () => {
  const qc = useQueryClient();
  return useMutation<
    StaffMember,
    Error,
    { id: string; data: UpdateStaffInput }
  >({
    mutationFn: ({ id, data }) =>
      put<StaffMember>(`/admin/rbac/staff/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STAFF_KEY });
      qc.invalidateQueries({ queryKey: ROLES_KEY });
    },
  });
};

export const useDeactivateStaff = () => {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id) => del<{ success: boolean }>(`/admin/rbac/staff/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAFF_KEY }),
  });
};
